import WidgetModel, { SendDataConfig } from '@mm/state/model/WidgetModel';
import { useViewActions } from '@mm/state/actions/viewActions';
import { StoreState } from '@mm/state/store-interfaces';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';

import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { selectWidgetFacebookAudienceConnections } from '@mm/state/selectors/connectionsSelectors';
import { ReactComponent as FacebookLogo } from 'assets/facebook-circle.svg';
import React, { ReactNode, useEffect } from 'react';
import classNames from 'classnames';
import { Handle, Position, NodeProps } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import css from './FacebookAds.module.css';
import common from '../index.module.css';

import { Count } from '../elements/Count/Count';
import { useOpenOnReady } from '../use-open-on-ready';
import { uniqueId } from 'lodash';

type Props = { className?: string } & NodeProps<{
  widget: WidgetModel;
}>;

export const FacebookAds: React.FC<Props> = ({
  className,
  data,
  isDragging,
  children,
}) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;
  const config = widget.config as Partial<SendDataConfig>;
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { connectionWebhookId } = config;
  const { retrieveWidgetConnections } = useConnectionCRUDActions();
  const viewActions = useViewActions();
  const connections =
    useSelector(selectWidgetFacebookAudienceConnections) ?? [];

  useEffect(() => {
    if (activeFbPageId) {
      retrieveWidgetConnections(activeFbPageId, 'facebook_ads_audience');
    }
  }, [activeFbPageId]);

  const connection = connections.find(c => c.id === connectionWebhookId);

  const onClick = () => {
    if (isDragging) {
      return;
    }
    viewActions.showSlideoutMenu('fbAdsIntegrationEditor', {
      widgetId: data.widget.id,
      adAudienceId: connection?.adAudienceId,
      adAudienceRemoteId: connection?.adAudience?.remoteId,
    });
  };

  useOpenOnReady(widget, onClick);

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <div
        className={classNames(className, common.card, css.container)}
        onClick={onClick}
      >
        <div className={css.body}>
          <div className={css.header}>
            <div className={css.icon}>
              <FacebookLogo />
            </div>

            <span className={css.placeholder}>Facebook Audience Sync</span>
            <div>
              {React.Children.map<ReactNode, ReactNode>(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    // @ts-ignore onEdit does not seemto be present or types are off in @types/react
                    onEdit: onClick,
                    key: uniqueId(),
                  });
                }
              })}
            </div>
          </div>
          {connection && (
            <div className={css.info}>
              <span className={css.connectionName}>
                {connection?.adAudience?.name}
              </span>

              <Count
                className={css.firesCount}
                value={`${data.widget.config?.firesCount || 0} contacts`}
              />
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} />
    </>
  );
};
